import { useEffect, useReducer, useState } from "react";
import { ChatCreateParams } from "../../../../pages/Home";
import { IChatParams } from "../../../../redux/chatSlice";
import { AppDispatch } from "../../../../redux/store";
import { chatService, IProject } from "../../../../services";
import { IMessage } from "../../types";

type MessageListAction =
  | { type: "SET_LIST"; payload: IMessage[] }
  | { type: "ADD_MESSAGE"; payload: IMessage }
  | {
      type: "ADD_MESSAGE_WITH_WELCOME";
      payload: { welcomeMessage: IMessage; newUserMessage: IMessage };
    }
  | { type: "REMOVE_LAST" }
  | { type: "RESET" };

function messageListReducer(state: IMessage[], action: MessageListAction): IMessage[] {
  switch (action.type) {
    case "SET_LIST":
      return action.payload;
    case "ADD_MESSAGE":
      return [...state, action.payload];
    case "ADD_MESSAGE_WITH_WELCOME": {
      const { welcomeMessage, newUserMessage } = action.payload;
      const hasWelcomeMessage = state.some((message) => message.content === welcomeMessage.content);
      if (hasWelcomeMessage) {
        return [...state, newUserMessage];
      } else {
        return [welcomeMessage, ...state, newUserMessage];
      }
    }
    case "REMOVE_LAST":
      return state.slice(0, -1);
    case "RESET":
      return []; // Ou retorne o valor inicial desejado
    default:
      return state;
  }
}

const useChatState = (
  dispatch: AppDispatch,
  onChatCreate: (chatUpdate: ChatCreateParams) => void,
  selectedChat?: IChatParams,
  selectedProject?: IProject,
  project_id?: number,
) => {
  const [showEmptyState, setShowEmptyState] = useState(true);

  const [messageList, dispatchMessage] = useReducer(messageListReducer, []);

  //Runs when a new chat is selected, updates the selected chat
  useEffect(() => {
    (async () => {
      if (!selectedChat || !project_id) {
        return;
      }
      if (!selectedChat.emptyChat && selectedChat.selectedChatId) {
        setShowEmptyState(false);
        const chat = await chatService.retrieveChat({
          projectId: project_id,
          sessionId: selectedChat.selectedChatId,
        });

        const welcomeMessage: IMessage = {
          role: "assistant",
          content: selectedProject?.welcome_message as string,
          date_time: new Date().toISOString(),
          references: undefined,
        };
        dispatchMessage({ type: "SET_LIST", payload: [welcomeMessage, ...chat.conversation] });
      } else {
        dispatchMessage({ type: "RESET" });
        setShowEmptyState(true);
      }
    })();
  }, [selectedChat]);

  const getOrCreateChatId = async (message?: string) => {
    if (!selectedChat || !project_id) {
      return;
    }

    if (selectedChat.emptyChat) {
      setShowEmptyState(false);
      const newChat = await chatService.createNewChat({
        projectId: project_id,
      });

      // Atualizando a sidebar com o chat novo
      onChatCreate({
        chat_id: newChat.id,
        project_id: project_id,
        knowledge_id: newChat.knowledge_id,
        message: message ?? "New chat",
      });

      return newChat.id;
    }
    return selectedChat.selectedChatId;
  };

  return {
    showEmptyState,
    messageList,
    getOrCreateChatId,
    dispatchMessage,
  };
};

export { useChatState };
